'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import { useQuery } from '@apollo/client'
import { DataTableManual } from '@/components/shared/data-table-manual/data-table-manual'
import { DataTableToolbar } from '@/components/shared/data-table-manual/data-table-toolbar-manual'
import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { LoginAttempt } from './types'
import { columns } from './components/columns'
import {
  FIND_MANY_LOGIN_ATTEMPT,
  FIND_MANY_LOGIN_ATTEMPT_COUNT,
} from './loginAttemptQueries'

export default function LoginAttempts() {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  })
  const [sorting, setSorting] = useState([
    {
      id: 'createdAt',
      desc: true,
    },
  ])
  const [columnFilters, setColumnFilters] = useState([])
  const [emailSearch, setEmailSearch] = useState('')
  const [filters, setFilters] = useState(new Map())

  // Track previous values to detect actual changes
  const prevEmailSearchRef = useRef(emailSearch)
  const prevFiltersRef = useRef(filters)

  // Reset pagination only when search or filters actually change
  useEffect(() => {
    if (emailSearch !== prevEmailSearchRef.current) {
      setPagination(prev => ({ ...prev, pageIndex: 0 }))
      prevEmailSearchRef.current = emailSearch
    }
  }, [emailSearch])

  useEffect(() => {
    if (filters !== prevFiltersRef.current) {
      setPagination(prev => ({ ...prev, pageIndex: 0 }))
      prevFiltersRef.current = filters
    }
  }, [filters])

  // Memoized callbacks to prevent unnecessary re-renders
  const handleSearch = useCallback((search: string) => {
    setEmailSearch(search)
  }, [])

  const handleFilter = useCallback((newFilters: Map<string, Set<any>>) => {
    setFilters(newFilters)
  }, [])

  // Create where clause for filtering
  const whereClause = (() => {
    const conditions: any = {}

    // Email search
    if (emailSearch) {
      conditions.email = {
        contains: emailSearch,
        mode: 'insensitive',
      }
    }

    // Type filter
    const typeFilter = filters.get('attempt_type')
    if (typeFilter && typeFilter.size > 0) {
      conditions.attempt_type = {
        in: Array.from(typeFilter),
      }
    }

    // Status filter
    const statusFilter = filters.get('status')
    if (statusFilter && statusFilter.size > 0) {
      conditions.status = {
        in: Array.from(statusFilter),
      }
    }

    return Object.keys(conditions).length > 0 ? conditions : undefined
  })()

  const {
    data: { findManyLoginAttempt: loginAttempts } = {},
    loading,
    error,
  } = useQuery(FIND_MANY_LOGIN_ATTEMPT, {
    variables: {
      where: whereClause,
      take: pagination.pageSize,
      skip: pagination.pageIndex * pagination.pageSize,
      orderBy: sorting.map(sort => ({
        [sort.id]: sort.desc ? 'desc' : 'asc',
      })),
    },
    fetchPolicy: 'cache-and-network',
  })

  const {
    data: { findManyLoginAttemptCount: totalCount } = {},
    loading: countLoading,
  } = useQuery(FIND_MANY_LOGIN_ATTEMPT_COUNT, {
    variables: {
      where: whereClause,
      orderBy: sorting.map(sort => ({
        [sort.id]: sort.desc ? 'desc' : 'asc',
      })),
    },
    fetchPolicy: 'cache-and-network',
  })

  if (error) {
    return (
      <PageContent>
        <div className="text-red-600">
          Error loading login attempts: {error.message}
        </div>
      </PageContent>
    )
  }

  // Memoized toolbar configuration to prevent unnecessary re-renders
  const toolbarConfig = useCallback(
    () => ({
      search: {
        onSearch: handleSearch,
        onFilter: handleFilter,
        placeholder: 'Search by email...',
        accessor: 'email',
      },
      columns: [
        {
          title: 'Type',
          accessor: 'attempt_type',
          options: [
            { label: 'Magic Link Request', value: 'MAGIC_LINK_REQUEST' },
            { label: 'Magic Link Verify', value: 'MAGIC_LINK_VERIFY' },
            { label: 'Email Password', value: 'EMAIL_PASSWORD' },
            { label: 'Impersonation', value: 'IMPERSONATION' },
            { label: 'Token Refresh', value: 'TOKEN_REFRESH' },
          ],
        },
        {
          title: 'Status',
          accessor: 'status',
          options: [
            { label: 'Success', value: 'SUCCESS' },
            { label: 'Failed', value: 'FAILED' },
            { label: 'Blocked', value: 'BLOCKED' },
            { label: 'Expired', value: 'EXPIRED' },
          ],
        },
      ],
    }),
    [handleSearch, handleFilter]
  )()

  return (
    <PageContent>
      <div className="flex justify-between items-center mb-8">
        <HeaderSection
          title="Login Attempts"
          subtitle="Monitor all login attempts and security events"
        />
      </div>

      <div className="space-y-4">
        <DataTableManual
          columns={columns}
          data={loginAttempts || []}
          rowCount={totalCount || 0}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          onColumnFiltersChange={setColumnFilters}
          defaultSorting={sorting}
          isLoading={loading || countLoading}
          emptyStateLabel="No login attempts found"
          tableTextSize="text-sm"
          toolbar={table => (
            <DataTableToolbar table={table} config={toolbarConfig} />
          )}
        />
      </div>
    </PageContent>
  )
}
